import { useLayoutEffect, useRef, useState } from 'react';
import type { Release } from '@/types';
import { StreamLinksList } from '@/components/list/StreamLinks';
import { UpcBarcode } from '@/components/ui/UpcBarcode';

type ListenMode = 'full' | 'compact' | 'icons';

/** Catalog / Date / Label / Listen (+ UPC) table for a release. */
export function ReleaseMetaDetails({
  release,
  className,
}: {
  release: Release;
  className?: string;
}) {
  const label = release.label?.trim() || '';
  const catalogNo = release.catalogNo?.trim() || '';
  const upc = release.upc?.trim() || '';
  const releaseDate = (() => {
    const d = new Date(release.releaseDate);
    if (!Number.isFinite(d.getTime())) return '';
    return d.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  })();
  const hasListen = Object.values(release.streamLinks || {}).some(Boolean);
  const listenRef = useRef<HTMLDivElement>(null);
  const [listenMode, setListenMode] = useState<ListenMode>('full');

  useLayoutEffect(() => {
    if (!hasListen) {
      setListenMode('full');
      return;
    }
    const row = listenRef.current;
    const dd = row?.querySelector('dd');
    const list = row?.querySelector('.stream-links-list');
    if (!row || !dd || !(list instanceof HTMLElement)) return;

    /** Links clip inside their flex child — measure the list, not the whole dd. */
    const fits = () => list.scrollWidth <= list.clientWidth + 1;

    const fit = () => {
      row.dataset.listenMode = 'full';
      if (fits()) {
        setListenMode((m) => (m === 'full' ? m : 'full'));
        return;
      }
      row.dataset.listenMode = 'compact';
      if (fits()) {
        setListenMode((m) => (m === 'compact' ? m : 'compact'));
        return;
      }
      row.dataset.listenMode = 'icons';
      setListenMode((m) => (m === 'icons' ? m : 'icons'));
    };

    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(dd);
    ro.observe(list);
    return () => ro.disconnect();
  }, [hasListen, upc, release.streamLinks]);

  if (!label && !catalogNo && !upc && !releaseDate && !hasListen) return null;

  return (
    <div
      className={['release-meta-panel', className].filter(Boolean).join(' ')}
    >
      <dl className="release-meta-list">
        {catalogNo ? (
          <div className="release-meta-item">
            <dt>Catalog</dt>
            <dd>{catalogNo}</dd>
          </div>
        ) : null}
        {releaseDate ? (
          <div className="release-meta-item">
            <dt>Date</dt>
            <dd>{releaseDate}</dd>
          </div>
        ) : null}
        {label ? (
          <div className="release-meta-item">
            <dt>Label</dt>
            <dd>{label}</dd>
          </div>
        ) : null}
        {hasListen || upc ? (
          <div
            ref={listenRef}
            className="release-meta-item release-meta-listen"
            data-listen-mode={listenMode}
          >
            <dt>Listen</dt>
            <dd>
              <StreamLinksList streamLinks={release.streamLinks} />
              {upc ? <UpcBarcode upc={upc} hideDigits /> : null}
            </dd>
          </div>
        ) : null}
      </dl>
    </div>
  );
}
